/**
 * Whether a read is in flight — the truth `aria-busy` has to tell (audit 2026-10-10, C7).
 *
 * A grey placeholder (a skeleton, a ghost table) is drawn in two quite different situations: a
 * read is under way and the data is about to land, or no read was ever started because the page
 * is waiting for the planner to choose something and the placeholder merely holds the shape of
 * what the choice will fill. `aria-busy="true"` is a promise that the region is about to change.
 * In the second case nothing will change until the planner acts, and twelve production pages
 * announced "busy" for as long as they stayed open.
 *
 * TanStack Query already knows. `isFetching` is true from the moment a read starts until it
 * settles, retries included, and false for a query that is disabled or idle. It is counted over the
 * whole client rather than over the page's own queries, because a placeholder does not know which
 * query it stands for; a page that reads nothing is not busy, and a page that is reading has the
 * placeholder busy. Outside a `QueryClientProvider` (a unit test of a primitive) there is nothing
 * to ask, and the answer is the conservative one: busy.
 *
 * **Foreground and background reads.** Not every read in flight is one the planner is waiting for. A
 * run's NAMES are read by walking its report, about two dozen sequential pages and a minute in
 * production, while the page already shows its codes: «codes first, names when they arrive». Such a
 * read names itself `BACKGROUND_READ` (per call, in the options of the hook that starts it) and is
 * left out of {@link useForegroundFetchingCount}, which is what a button's busy state and a
 * placeholder's `aria-busy` ask — they must settle when the page's own data has. The shell's
 * progress line keeps asking {@link useFetchingCount}, which counts everything: it is truthful that
 * something is still loading.
 */

import { QueryClientContext, type Query } from '@tanstack/react-query';
import { useContext, useSyncExternalStore } from 'react';

const NEVER = (): (() => void) => () => undefined;

/**
 * The query `meta` of a read nobody waits for. Spread into the options of ONE call, like
 * `SESSION_LONG_READ`; the only place the flag is spelled.
 */
export const BACKGROUND_READ = { meta: { background: true } } as const;

/** `true` for a read the planner is waiting for: anything that did not name itself background. */
function isForegroundRead(query: Query): boolean {
  return query.meta?.['background'] !== true;
}

function useCount(foregroundOnly: boolean): number | null {
  const client = useContext(QueryClientContext);
  return useSyncExternalStore(
    client === undefined ? NEVER : (onChange) => client.getQueryCache().subscribe(onChange),
    () => (client === undefined ? null : client.isFetching(foregroundOnly ? { predicate: isForegroundRead } : undefined)),
    () => null,
  );
}

/**
 * How many reads are in flight, or `null` when there is no query client to ask (a unit test of a
 * primitive). Retries count: a read that is retrying is still being read.
 */
export function useFetchingCount(): number | null {
  return useCount(false);
}

/**
 * How many FOREGROUND reads are in flight — {@link useFetchingCount} less the ones that named
 * themselves `BACKGROUND_READ`; `null` when there is no query client to ask.
 */
export function useForegroundFetchingCount(): number | null {
  return useCount(true);
}

/**
 * `true` while a foreground read is in flight; `true` when there is no client to ask. A background
 * read (the run's names) does not make a skeleton or a ghost table `aria-busy`: the region it
 * guards is not about to change because of it.
 */
export function useReadInFlight(): boolean {
  const count = useForegroundFetchingCount();
  return count === null || count > 0;
}
